// Self-contained markdown → React renderer for the Help page.
//
// Why hand-rolled instead of a library: the runtime CSP is `style-src 'self'`
// (no unsafe-inline) and the SPA renders exclusively through React inline
// `style` objects (CSSOM property assignment, which CSP does not block). A
// markdown library that emits raw HTML via `dangerouslySetInnerHTML` would
// need injected `<style>`/`style=""` and defeats the XSS posture. Rendering to
// React elements keeps everything on the sanctioned path and adds no
// dependency. The doc corpus uses a small, known markdown subset (surveyed
// against docs/user-guide + docs/runbooks): headings, paragraphs, ordered/
// unordered lists with one nesting level, fenced + inline code, bold/italic,
// links, tables, blockquotes.

import type { ReactNode } from 'react';
import type { CSSProperties } from 'react';
import { C } from '../lib/theme';
import type { HelpDoc } from './docs';

// ---------------------------------------------------------------------------
// Link classification + rewrite
// ---------------------------------------------------------------------------

export interface LinkTarget {
  /** 'external' → real <a> (new tab). 'doc' → in-app jump to a bundled doc.
   *  'anchor' → in-app jump to a heading in the current doc. 'plain' → the
   *  target isn't resolvable in-app (repo-relative to code / unbundled docs),
   *  so render the link text without a link. */
  kind: 'external' | 'doc' | 'anchor' | 'plain';
  /** For 'doc': the target doc id. For 'anchor': the heading slug. For
   *  'external': the href. */
  ref?: string;
}

export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

// `knownDocIds` is the set of doc ids bundled in the SAME group as the doc
// being rendered (guide links resolve to guide docs; runbook links to runbooks).
export function classifyLink(href: string, knownDocIds: Set<string>): LinkTarget {
  if (/^(https?:|mailto:)/i.test(href)) return { kind: 'external', ref: href };
  if (href.startsWith('#')) {
    const anchor = href.slice(1);
    return anchor ? { kind: 'anchor', ref: anchor } : { kind: 'plain' };
  }
  // Relative `.md` link — resolvable only if it points at a same-group bundled
  // doc (e.g. `03-vetting.md`, `admin-account-lifecycle.md#anchor`). Repo-
  // relative escapes (`../deployment/...`, `../../apps/...`) are not bundled.
  const mdMatch = /^([^/#][^/]*?)\.md(#.*)?$/i.exec(href);
  if (mdMatch) {
    const id = mdMatch[1];
    if (knownDocIds.has(id)) return { kind: 'doc', ref: id };
  }
  return { kind: 'plain' };
}

// ---------------------------------------------------------------------------
// Inline parsing: code, bold, italic, links
// ---------------------------------------------------------------------------

const codeStyle: CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '0.9em',
  background: C.bgInput,
  border: `1px solid ${C.border}`,
  borderRadius: 4,
  padding: '1px 5px',
};

interface InlineCtx {
  knownDocIds: Set<string>;
  onNavigate: (docId: string) => void;
  highlight?: string[];
}

// Highlight query terms inside a plain-text run (used in search snippets and
// when a search result is opened). Case-insensitive, whole/prefix substrings.
function highlightText(text: string, terms: string[] | undefined, keyBase: string): ReactNode[] {
  if (!terms || terms.length === 0) return [text];
  const escaped = terms.filter(Boolean).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (escaped.length === 0) return [text];
  // Split with a capture group: matched delimiters land at odd indices.
  const re = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(re);
  return parts.map((part, i) =>
    i % 2 === 1
      ? (
          <mark key={`${keyBase}-h${i}`} style={{ background: C.goldLight, color: C.black, borderRadius: 2, padding: '0 1px' }}>
            {part}
          </mark>
        )
      : (
          part
        ),
  );
}

function parseInline(text: string, ctx: InlineCtx, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let i = 0;
  let buf = '';
  let key = 0;

  const flush = () => {
    if (buf) {
      nodes.push(...highlightText(buf, ctx.highlight, `${keyBase}-t${key++}`));
      buf = '';
    }
  };

  while (i < text.length) {
    const rest = text.slice(i);

    // Inline code — highest precedence, no nested formatting.
    const code = /^`([^`]+)`/.exec(rest);
    if (code) {
      flush();
      nodes.push(<code key={`${keyBase}-c${key++}`} style={codeStyle}>{code[1]}</code>);
      i += code[0].length;
      continue;
    }

    // Link [text](href)
    const link = /^\[([^\]]+)\]\(([^)]+)\)/.exec(rest);
    if (link) {
      flush();
      const [, label, href] = link;
      const target = classifyLink(href.trim(), ctx.knownDocIds);
      const labelNodes = parseInline(label, ctx, `${keyBase}-l${key}`);
      if (target.kind === 'external') {
        nodes.push(
          <a key={`${keyBase}-a${key++}`} href={target.ref} target="_blank" rel="noopener noreferrer" style={{ color: C.goldText }}>
            {labelNodes}
          </a>,
        );
      } else if (target.kind === 'doc') {
        nodes.push(
          <button
            key={`${keyBase}-a${key++}`}
            type="button"
            onClick={() => ctx.onNavigate(target.ref!)}
            style={{ background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', color: C.goldText, cursor: 'pointer', textDecoration: 'underline' }}
          >
            {labelNodes}
          </button>,
        );
      } else if (target.kind === 'anchor') {
        nodes.push(
          <a key={`${keyBase}-a${key++}`} href={`#${target.ref}`} style={{ color: C.goldText }}>
            {labelNodes}
          </a>,
        );
      } else {
        // Not resolvable in-app — render the label as plain text.
        nodes.push(<span key={`${keyBase}-a${key++}`}>{labelNodes}</span>);
      }
      i += link[0].length;
      continue;
    }

    // Bold **text** or __text__
    const bold = /^(\*\*|__)([^*_].*?)\1/.exec(rest);
    if (bold) {
      flush();
      nodes.push(<strong key={`${keyBase}-b${key++}`}>{parseInline(bold[2], ctx, `${keyBase}-b${key}`)}</strong>);
      i += bold[0].length;
      continue;
    }

    // Italic *text* or _text_ (single delimiter, not part of a bold run).
    const italic = /^(\*|_)([^*_\s][^*_]*?)\1/.exec(rest);
    if (italic) {
      flush();
      nodes.push(<em key={`${keyBase}-i${key++}`}>{parseInline(italic[2], ctx, `${keyBase}-i${key}`)}</em>);
      i += italic[0].length;
      continue;
    }

    buf += text[i];
    i += 1;
  }
  flush();
  return nodes;
}

// ---------------------------------------------------------------------------
// Block parsing
// ---------------------------------------------------------------------------

const headingStyles: Record<number, CSSProperties> = {
  1: { fontSize: 24, fontWeight: 700, color: C.text, margin: '4px 0 16px' },
  2: { fontSize: 19, fontWeight: 700, color: C.text, margin: '28px 0 12px', paddingTop: 8, borderTop: `1px solid ${C.border}` },
  3: { fontSize: 16, fontWeight: 600, color: C.text, margin: '20px 0 10px' },
  4: { fontSize: 14, fontWeight: 600, color: C.textSecondary, margin: '16px 0 8px' },
  5: { fontSize: 13, fontWeight: 600, color: C.textSecondary, margin: '14px 0 6px' },
  6: { fontSize: 13, fontWeight: 600, color: C.textMuted, margin: '12px 0 6px' },
};

function isTableRow(line: string): boolean {
  return /^\s*\|.*\|\s*$/.test(line);
}
function isTableDivider(line: string): boolean {
  return /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes('-');
}
function splitTableCells(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());
}

export interface RenderCtx {
  doc: HelpDoc;
  knownDocIds: Set<string>;
  onNavigate: (docId: string) => void;
  highlight?: string[];
}

export function renderMarkdown(ctx: RenderCtx): ReactNode {
  const inlineCtx: InlineCtx = { knownDocIds: ctx.knownDocIds, onNavigate: ctx.onNavigate, highlight: ctx.highlight };
  // Drop the leading H1 — the page shows the doc title as its own header.
  const lines = ctx.doc.body.replace(/^\uFEFF/, '').split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let li = 0;
  let key = 0;
  let firstH1Dropped = false;

  const paragraph = (text: string) => (
    <p key={`p${key++}`} style={{ margin: '0 0 12px', lineHeight: 1.6, color: C.text }}>
      {parseInline(text, inlineCtx, `p${key}`)}
    </p>
  );

  while (li < lines.length) {
    const line = lines[li];

    // Blank line
    if (/^\s*$/.test(line)) {
      li++;
      continue;
    }

    // Fenced code block
    const fence = /^(\s*)(```|~~~)(.*)$/.exec(line);
    if (fence) {
      const closer = fence[2];
      const codeLines: string[] = [];
      li++;
      while (li < lines.length && !new RegExp(`^\\s*${closer}\\s*$`).test(lines[li])) {
        codeLines.push(lines[li]);
        li++;
      }
      li++; // consume closing fence
      blocks.push(
        <pre key={`pre${key++}`} style={{ background: C.bgSidebar, color: '#e5e7eb', padding: '12px 14px', borderRadius: 8, overflowX: 'auto', fontSize: 12.5, lineHeight: 1.5, margin: '0 0 14px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
          <code>{codeLines.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // Heading
    const heading = /^(#{1,6})\s+(.+?)\s*$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2];
      li++;
      if (level === 1 && !firstH1Dropped) {
        firstH1Dropped = true;
        continue;
      }
      const id = slugifyHeading(text);
      // Always render an <h2> element (the doc title is the page's <h1>); the
      // visual level comes from headingStyles so the document outline stays
      // single-h1 for a11y.
      blocks.push(
        <h2 key={`h${key++}`} id={id} style={headingStyles[level]}>
          {parseInline(text, inlineCtx, `h${key}`)}
        </h2>,
      );
      continue;
    }

    // Blockquote (consume consecutive `>` lines)
    if (/^\s*>/.test(line)) {
      const quoteLines: string[] = [];
      while (li < lines.length && /^\s*>/.test(lines[li])) {
        quoteLines.push(lines[li].replace(/^\s*>\s?/, ''));
        li++;
      }
      blocks.push(
        <blockquote key={`bq${key++}`} style={{ borderLeft: `3px solid ${C.gold}`, background: C.goldBg, margin: '0 0 14px', padding: '8px 14px', color: C.textSecondary }}>
          {parseInline(quoteLines.join(' '), inlineCtx, `bq${key}`)}
        </blockquote>,
      );
      continue;
    }

    // Table
    if (isTableRow(line) && li + 1 < lines.length && isTableDivider(lines[li + 1])) {
      const header = splitTableCells(line);
      li += 2; // header + divider
      const rows: string[][] = [];
      while (li < lines.length && isTableRow(lines[li])) {
        rows.push(splitTableCells(lines[li]));
        li++;
      }
      blocks.push(
        <div key={`tbl${key++}`} style={{ overflowX: 'auto', margin: '0 0 14px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {header.map((h, ci) => (
                  <th key={ci} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${C.border}`, color: C.textMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {parseInline(h, inlineCtx, `th${key}-${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {header.map((_, ci) => (
                    <td key={ci} style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, verticalAlign: 'top' }}>
                      {parseInline(r[ci] ?? '', inlineCtx, `td${key}-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Lists (ordered / unordered) with one level of nesting via indentation.
    const listItem = /^(\s*)([-*+]|\d+\.)\s+(.*)$/.exec(line);
    if (listItem) {
      const ordered = /\d+\./.test(listItem[2]);
      const items: { indent: number; text: string }[] = [];
      while (li < lines.length) {
        const m = /^(\s*)([-*+]|\d+\.)\s+(.*)$/.exec(lines[li]);
        if (m && /\d+\./.test(m[2]) === ordered) {
          items.push({ indent: m[1].length, text: m[3] });
          li++;
        } else if (m) {
          // A list of the other kind starts — stop this list.
          break;
        } else if (/^\s+\S/.test(lines[li]) && items.length > 0) {
          // Continuation line for the previous item.
          items[items.length - 1].text += ' ' + lines[li].trim();
          li++;
        } else {
          break;
        }
      }
      blocks.push(renderList(items, ordered, inlineCtx, `list${key++}`));
      continue;
    }

    // Horizontal rule
    if (/^\s*(---|\*\*\*|___)\s*$/.test(line)) {
      li++;
      blocks.push(<hr key={`hr${key++}`} style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '20px 0' }} />);
      continue;
    }

    // Paragraph — gather consecutive non-blank, non-block lines.
    const paraLines: string[] = [line];
    li++;
    while (
      li < lines.length &&
      !/^\s*$/.test(lines[li]) &&
      !/^(#{1,6})\s/.test(lines[li]) &&
      !/^\s*>/.test(lines[li]) &&
      !/^(\s*)([-*+]|\d+\.)\s+/.test(lines[li]) &&
      !/^(\s*)(```|~~~)/.test(lines[li]) &&
      !isTableRow(lines[li])
    ) {
      paraLines.push(lines[li]);
      li++;
    }
    blocks.push(paragraph(paraLines.join(' ')));
  }

  return <div>{blocks}</div>;
}

// Build a nested list. Items carry an indent width; the shallowest indent is
// the top level, deeper indents nest one <ul>/<ol> under the preceding item.
function renderList(
  items: { indent: number; text: string }[],
  ordered: boolean,
  ctx: InlineCtx,
  keyBase: string,
): ReactNode {
  const baseIndent = Math.min(...items.map((i) => i.indent));
  const Tag = ordered ? 'ol' : 'ul';
  const topStyle: CSSProperties = { margin: '0 0 12px', paddingLeft: 22, lineHeight: 1.6, color: C.text };

  const children: ReactNode[] = [];
  let idx = 0;
  let key = 0;
  while (idx < items.length) {
    const item = items[idx];
    // Collect any deeper-indented items that belong under this one.
    const nested: { indent: number; text: string }[] = [];
    let j = idx + 1;
    while (j < items.length && items[j].indent > baseIndent) {
      nested.push(items[j]);
      j++;
    }
    children.push(
      <li key={`${keyBase}-${key++}`} style={{ marginBottom: 4 }}>
        {parseInline(item.text, ctx, `${keyBase}-li${key}`)}
        {nested.length > 0 && renderList(nested, ordered, ctx, `${keyBase}-n${key}`)}
      </li>,
    );
    idx = j;
  }

  return (
    <Tag key={keyBase} style={topStyle}>
      {children}
    </Tag>
  );
}
